import { useMemo } from "react";
import type { MatchEvent } from "../../../store/useMatchLogStore";
import type { Match } from "../../logger/types";
import { PITCH_HEIGHT, PITCH_WIDTH } from "../../logger/utils/heatMapZones";
import {
  PITCH_MARKINGS,
  PITCH_MARKINGS_DERIVED,
} from "../../logger/utils/pitchGeometry";
import { deriveBallPath, trailSegments } from "../ballPath";

interface Props {
  match: Match;
  events: MatchEvent[];
  options: Record<string, unknown>;
}

const LINE = "rgba(255,255,255,0.85)";
const HOME_FALLBACK = "#38bdf8";
const AWAY_FALLBACK = "#fb923c";
const SHOT_COLOR = "#f43f5e";

const num = (v: unknown, d: number) =>
  typeof v === "number" && Number.isFinite(v) ? v : d;

export default function LivePitch({ match, events, options }: Props) {
  const trailLength = Math.max(0, Math.min(10, num(options.trail_length, 5)));
  const teamColors = options.use_team_colors !== false;
  const playerLabels = options.show_player_labels === true;
  const homeId = match.home_team.team_id;

  const path = useMemo(() => deriveBallPath(events, homeId), [events, homeId]);
  const trails = useMemo(
    () => trailSegments(path.segments, trailLength),
    [path.segments, trailLength],
  );

  // EPIC-112: the match kit's shirt colour wins over the team colour. Not
  // routed through resolveTeamPalette: that hashes a primary-only team to a
  // palette colour, which would change this widget for teams without a kit.
  const colorFor = (teamId: string) => {
    if (!teamColors) return "#ffffff";
    const home = teamId === homeId;
    const side = home ? match.home_team : match.away_team;
    return (
      side.kit?.shirt_color ||
      side.primary_color ||
      (home ? HOME_FALLBACK : AWAY_FALLBACK)
    );
  };

  const jerseyFor = (playerId?: string) => {
    if (!playerId) return "";
    const p =
      match.home_team.players.find((x) => x.id === playerId) ??
      match.away_team.players.find((x) => x.id === playerId);
    return p ? `#${p.jersey_number}` : "";
  };

  const m = PITCH_MARKINGS;
  const d = PITCH_MARKINGS_DERIVED;
  const lastSeg = trails[trails.length - 1];

  return (
    <svg
      data-testid="broadcast-live-pitch"
      viewBox={`-3 -3 ${PITCH_WIDTH + 6} ${PITCH_HEIGHT + 6}`}
      width="100%"
      height="100%"
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label="Live pitch"
    >
      <defs>
        <linearGradient id="bcast-grass" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#1f6b3a" />
          <stop offset="1" stopColor="#17542d" />
        </linearGradient>
        <marker
          id="bcast-arrow"
          viewBox="0 0 6 6"
          refX="5"
          refY="3"
          markerWidth="3.5"
          markerHeight="3.5"
          orient="auto-start-reverse"
        >
          <path d="M0,0 L6,3 L0,6 z" fill="context-stroke" />
        </marker>
      </defs>
      <rect
        width={PITCH_WIDTH}
        height={PITCH_HEIGHT}
        fill="url(#bcast-grass)"
        rx="1.5"
      />
      <g stroke={LINE} strokeWidth="0.35" fill="none">
        <rect
          x="0.5"
          y="0.5"
          width={PITCH_WIDTH - 1}
          height={PITCH_HEIGHT - 1}
        />
        <line
          x1={PITCH_WIDTH / 2}
          y1="0.5"
          x2={PITCH_WIDTH / 2}
          y2={PITCH_HEIGHT - 0.5}
        />
        <ellipse
          cx={PITCH_WIDTH / 2}
          cy={PITCH_HEIGHT / 2}
          rx={m.centerCircleRx}
          ry={m.centerCircleRy}
        />
        <rect
          x="0.5"
          y={d.penaltyAreaY}
          width={m.penaltyAreaDepth}
          height={m.penaltyAreaHeight}
        />
        <rect
          x={PITCH_WIDTH - 0.5 - m.penaltyAreaDepth}
          y={d.penaltyAreaY}
          width={m.penaltyAreaDepth}
          height={m.penaltyAreaHeight}
        />
        <rect
          x="0.5"
          y={d.goalAreaY}
          width={m.goalAreaDepth}
          height={m.goalAreaHeight}
        />
        <rect
          x={PITCH_WIDTH - 0.5 - m.goalAreaDepth}
          y={d.goalAreaY}
          width={m.goalAreaDepth}
          height={m.goalAreaHeight}
        />
      </g>

      {trails.map((s) => {
        const color = s.kind === "shot" ? SHOT_COLOR : colorFor(s.teamId);
        return (
          <line
            key={s.id}
            data-testid="broadcast-trail"
            data-kind={s.kind}
            x1={s.from[0]}
            y1={s.from[1]}
            x2={s.to[0]}
            y2={s.to[1]}
            stroke={color}
            strokeOpacity={s.kind === "move" ? s.opacity * 0.5 : s.opacity}
            strokeWidth={s.kind === "shot" ? 1.1 : 0.8}
            strokeLinecap="round"
            strokeDasharray={s.kind === "move" ? "1.2 1.6" : undefined}
            markerEnd={s.kind === "move" ? undefined : "url(#bcast-arrow)"}
          />
        );
      })}

      {playerLabels && lastSeg && jerseyFor(lastSeg.playerId) && (
        <text
          x={lastSeg.from[0]}
          y={lastSeg.from[1] - 2}
          fontSize="3.4"
          fontWeight="700"
          textAnchor="middle"
          fill="#ffffff"
          stroke="rgba(0,0,0,0.7)"
          strokeWidth="0.5"
          paintOrder="stroke"
        >
          {jerseyFor(lastSeg.playerId)}
        </text>
      )}

      {path.ball && (
        <g
          data-testid="broadcast-ball"
          style={{
            transform: `translate(${path.ball[0]}px, ${path.ball[1]}px)`,
            transition: "transform 700ms cubic-bezier(0.22, 1, 0.36, 1)",
          }}
        >
          <circle r="2" fill="#ffffff" stroke="#0f172a" strokeWidth="0.5" />
        </g>
      )}
    </svg>
  );
}
